import { useIntl } from 'react-intl';
import { ID } from '@rossum/api-client';
import { notify } from '../../../toaster';
import { CredentialFormValues, toCredentialSecret } from '../formSchema';
import { Credential, CredentialTestResult } from '../types';
import { useCreateCredential } from './useCreateCredential';
import { usePatchCredential } from './usePatchCredential';
import { useTestCredential } from './useTestCredential';

const useTestFailureMessage = () => {
  const intl = useIntl();

  return (result: CredentialTestResult) => {
    if (result.missingScopes && result.missingScopes.length > 0) {
      return intl.formatMessage(
        { id: 'features.credentials.form.test.missingScopes' },
        { scopes: result.missingScopes.join(', ') }
      );
    }

    return intl.formatMessage({ id: 'features.credentials.form.test.failed' });
  };
};

type UseSubmitCredentialParams = {
  /** Existing credential when editing, `undefined` when creating a new one. */
  credentialId?: ID;
  onSuccess: (credential: Credential) => void;
};

/**
 * Credentials are always verified against the target system before they are
 * stored - the form is only submitted when the test endpoint reports `ok`.
 */
export const useSubmitCredential = ({
  credentialId,
  onSuccess,
}: UseSubmitCredentialParams) => {
  const intl = useIntl();
  const getTestFailureMessage = useTestFailureMessage();

  const { mutateAsync: testCredential } = useTestCredential();
  const { mutateAsync: createCredential } = useCreateCredential();
  const { mutateAsync: patchCredential } = usePatchCredential(credentialId);

  const isEditing = credentialId !== undefined;

  const submit = async (values: CredentialFormValues) => {
    const secret = toCredentialSecret(values);

    try {
      const testResult = await testCredential({
        credentialId,
        type: values.type,
        secret,
      });

      if (testResult.status !== 'ok') {
        notify.error({ title: getTestFailureMessage(testResult) });

        return;
      }

      const credential = isEditing
        ? await patchCredential({ name: values.name, secret })
        : await createCredential({
            name: values.name,
            type: values.type,
            secret,
          });

      notify.default({
        title: intl.formatMessage({
          id: isEditing
            ? 'features.credentials.form.notification.updated'
            : 'features.credentials.form.notification.created',
        }),
      });

      onSuccess(credential);
    } catch {
      notify.error({
        title: intl.formatMessage({
          id: isEditing
            ? 'features.credentials.form.notification.updateFailed'
            : 'features.credentials.form.notification.createFailed',
        }),
      });
    }
  };

  return { submit };
};
